import { Button, Progress } from '@heroui/react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useGetPatientQuery } from '@/patient-app-common/api/patientApi';
import { usePostTrackerMutation } from '@/patient-app-common/api/trackerApi';
import { reviewHelperContext } from '@/patient-app-common/helpers/survey_helper';
import useEducation from '@/patient-app-common/hooks/useEducation';
import { useAppSelector } from '@/redux/hooks';

import { SurveyLoader } from '../layout/SurveyLoader';

import { createOpioidParams, getOpioidRisk, getAvgRisk } from './OpioidRisk';

export function OpioidReview({
  answers,
  eventId,
  idempotencyKey,
  profile,
  questions,
  surveyData,
}) {
  const { token } = useAppSelector(({ auth }) => auth);
  const { getEducationForSurvey } = useEducation({ skip: true });

  const { t } = useTranslation('survey');

  const navigate = useNavigate();
  const submittingRef = useRef(false);

  const { data: patient } = useGetPatientQuery();

  const [submitTracker] = usePostTrackerMutation();

  const [loader, showLoader] = useState(null);
  const [reviewHelper, setReviewHelper] = useState(null);

  useEffect(() => {
    const helper = reviewHelperContext({
      surveyData,
      answers,
      profile,
      medicineHistory: null,
    });
    setReviewHelper(helper);
  }, [answers, profile, surveyData]);

  const createOpioidResult = async () => {
    const education = await getEducationForSurvey(surveyData.id, true);

    const opioidParams = createOpioidParams(
      patient.procedureName,
      questions,
      answers
    );
    const result = getOpioidRisk(opioidParams);
    const average = getAvgRisk(opioidParams);

    return { result, average, education };
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;

    submittingRef.current = true;
    showLoader({ text: t('review.loading_text') });

    const params = reviewHelper.createSurveyParams('app_event', eventId);
    params.token = token;
    params.idempotencyKey = idempotencyKey.current;

    try {
      const { result, average, education } = await createOpioidResult();
      const res = await submitTracker(params).unwrap();

      if (res.submitted) {
        showLoader(false);

        navigate('/opioidrisk', {
          state: {
            result,
            average,
            education,
          },
        });
      } else {
        submittingRef.current = false;
        showLoader({
          text: t('common.error.server'),
          type: 'error',
        });
      }
    } catch (e) {
      submittingRef.current = false;
      console.error(e);
      showLoader({
        text: t('common.error.server'),
        type: 'error',
      });
    }
  };

  if (loader) {
    return (
      <SurveyLoader
        text={loader.text}
        type={loader.type}
        handleBack={() => showLoader(null)}
      />
    );
  }

  return (
    <div className="flex flex-col min-h-[100dvh]">
      <header className="bg-background">
        <div className="flex justify-between p-4 items-center">
          <h1 className="font-light text-xl">{t('review.title')}</h1>
        </div>
        <Progress
          aria-label="100%"
          className=""
          value={100}
          color="success"
          radius="none"
        />
      </header>
      <main className="flex justify-center">
        <div className="flex flex-col w-full max-w-[800px] text-left p-12 space-y-4">
          <h2 className="font-bold">{t('review.title')}</h2>
        </div>
      </main>
      <footer className="flex justify-center p-4 space-x-4">
        <Button
          className="w-1/2 md:w-1/4"
          color="primary"
          onPress={handleSubmit}
          size="lg"
        >
          {t('common.action.submit')}
        </Button>
      </footer>
    </div>
  );
}
